/**
 * crm/LeadCard.jsx — Phase 75 refactor
 * Extracted from CRMShell.jsx (Phase 70 + 74). Single Kanban lead card with
 * inline-editable company/contact, type/service/ICP chips, value + age.
 */
import React from "react";
import { GripVertical, Trash2 } from "lucide-react";
import { InlineText, daysAgo, inr, LEAD_TYPE_TONE, ICP_TONE } from "./_shared";

export function LeadCard({ lead, canEdit, onDragStart, onDelete, onPatch, onOpen }) {
  const age = daysAgo(lead.created_at);
  return (
    <div
      draggable={canEdit}
      onDragStart={(e) => onDragStart(e, lead.id)}
      onClick={(e) => {
        // Don't open the drawer when the user clicks an interactive child.
        if (e.target.closest("input, select, button, a")) return;
        onOpen?.(lead);
      }}
      className="group bg-white border border-slate-200 rounded-sm p-2.5 text-sm hover:border-[#F54800]/50 hover:shadow-sm transition-all cursor-grab active:cursor-grabbing"
      data-testid={`crm-lead-card-${lead.id}`}
    >
      <div className="flex items-start gap-1.5">
        {canEdit && <GripVertical size={11} className="text-slate-300 mt-0.5 shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <InlineText
              value={lead.company_name || ""}
              onSave={(v) => onPatch(lead.id, "company_name", v)}
              placeholder="Company"
              testId={`crm-lead-company-${lead.id}`}
            />
          </div>
          {lead.code && (
            <div className="text-[9px] font-mono uppercase tracking-wider text-slate-400 mt-0.5">{lead.code}</div>
          )}
          <div className="text-[11px] text-slate-500 mt-0.5">
            <InlineText
              value={lead.contact_name || ""}
              onSave={(v) => onPatch(lead.id, "contact_name", v)}
              placeholder="Add contact"
            />
          </div>
          {/* Phase 74 chips: Lead Type, ICP (with red Student) */}
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            {lead.lead_type && (
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-sm ${LEAD_TYPE_TONE[lead.lead_type] || "bg-slate-100 text-slate-700"}`} data-testid={`crm-lead-card-type-${lead.id}`}>
                {lead.lead_type}
              </span>
            )}
            {lead.service && (
              <span className="text-[9px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm">{lead.service}</span>
            )}
            {lead.icp && (
              <span className={`text-[9px] ${ICP_TONE(lead.icp)}`} title={lead.icp}>
                {lead.icp.startsWith("ICP") ? lead.icp.split(" - ")[0] : lead.icp}
              </span>
            )}
          </div>
          {(lead.lead_value || lead.expected_close_date || age !== null) && (
            <div className="flex items-center gap-2 mt-1.5 text-[10px] text-slate-500">
              {lead.lead_value > 0 && (
                <span className="bg-slate-100 px-1.5 py-0.5 rounded-sm font-medium text-slate-700">
                  {inr(lead.lead_value)}
                </span>
              )}
              {lead.expected_close_date && (
                <span className="text-slate-400">{lead.expected_close_date}</span>
              )}
              {age !== null && (
                <span className={`${age > 14 ? "text-rose-600" : age > 7 ? "text-amber-600" : "text-slate-400"}`} title="Lead age in days">
                  {age}d old
                </span>
              )}
            </div>
          )}
        </div>
        {canEdit && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(lead.id); }}
            className="text-slate-300 hover:text-red-600 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
            data-testid={`crm-lead-delete-${lead.id}`}
            title="Delete lead"
          >
            <Trash2 size={11} />
          </button>
        )}
      </div>
    </div>
  );
}
